import { useEffect, useMemo, useState } from 'react';
import type { Programme } from '../types';
import { listProgrammes, createProgramme } from '../lib/api';
import { IconSearch, IconPlus, IconRefresh, IconChevronRight } from '../components/icons';
import { SkeletonRows } from '../components/Skeleton';
import { useToast } from '../lib/toast';
import { ProgrammeEditDrawer } from './ProgrammeEditDrawer';
import { relativeDate } from '../lib/format';

interface Props {
  onCountChange?: (n: number) => void;
}

const PUBLI_STATUS = ['', 'Draft', 'Publié', 'Archivé', 'Rejeté'];
const COUNTRIES = ['', 'Canada', 'United States', 'France', 'United Kingdom', 'European Union', 'Other'];
const INSTRUMENT_TYPES = ['', 'Subvention', 'Prêt', 'Crédit d\'impôt', 'Garantie', 'Bourse', 'Avance remboursable', 'Mixte'];

const STATUS_STYLE: Record<string, string> = {
  Draft: 'bg-amber-soft text-amber',
  Publié: 'bg-funds-soft text-funds-deep',
  Archivé: 'bg-cream-deep text-navy',
  Rejeté: 'bg-red-50 text-red-700',
};

/**
 * Page Programmes non-dilutifs (PR 3).
 *
 * Liste + filtres + drawer édition. Pas de tri serveur Phase 3 (les programmes
 * sont ~30, trié côté serveur par nom). Filtres client side.
 *
 * Bilingue : la table affiche Nom FR par défaut avec fallback Nom EN, et
 * signale en pictogramme amber les records qui n'ont pas leur paire FR+EN
 * complète (dette de parité bilingue, cf. P7 brief V2).
 */
export function ProgrammesPage({ onCountChange }: Props) {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Programme | null>(null);
  const [search, setSearch] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInstrument, setFilterInstrument] = useState('');
  const [creating, setCreating] = useState(false);
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listProgrammes();
      setProgrammes(res.programmes);
      onCountChange?.(res.programmes.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load programmes');
      setProgrammes([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return programmes.filter((p) => {
      if (q) {
        const blob = `${p.nameFr ?? ''} ${p.nameEn ?? ''} ${p.operator ?? ''} ${p.idProgramme ?? ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (filterCountry && p.country !== filterCountry) return false;
      if (filterStatus && p.publicationStatus !== filterStatus) return false;
      if (filterInstrument && p.instrumentType !== filterInstrument) return false;
      return true;
    });
  }, [programmes, search, filterCountry, filterStatus, filterInstrument]);

  function onSaved(updated: Programme) {
    setProgrammes((list) => list.map((p) => (p.id === updated.id ? updated : p)));
    setEditing(updated);
  }

  function onDeleted(id: string) {
    setProgrammes((list) => list.filter((p) => p.id !== id));
    onCountChange?.(programmes.length - 1);
    setEditing(null);
  }

  async function onCreate() {
    if (creating) return;
    setCreating(true);
    try {
      const res = await createProgramme({
        nameFr: 'Nouveau programme',
        nameEn: 'New programme',
        publicationStatus: 'Draft',
      });
      setProgrammes((list) => [res.programme, ...list]);
      setEditing(res.programme);
      onCountChange?.(programmes.length + 1);
      toast.success('Programme créé', 'Brouillon prêt à éditer');
    } catch (e) {
      toast.error('Création échouée', e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  function formatAmount(n?: number) {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '-';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toLocaleString('fr-FR');
  }

  return (
    <div className="px-4 md:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-display tracking-tightish text-ink">
            Programmes non-dilutifs
          </h1>
          <p className="mt-1 text-[13.5px] text-ink-muted max-w-2xl">
            CRUD bilingue des programmes de financement non-dilutifs (subventions, prêts, crédits
            d&apos;impôt). Servis sur financeforquantum.com/non-dilutive/.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <IconRefresh className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button
            onClick={onCreate}
            disabled={creating}
            className="h-9 px-3.5 rounded-md bg-navy text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-navy-soft transition disabled:opacity-60"
          >
            <IconPlus className="w-4 h-4" />
            {creating ? 'Création...' : 'Add programme'}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          <span className="font-medium">Erreur : </span>{error}
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-line-soft bg-cream p-3 shadow-card">
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Recherche</label>
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, opérateur, ID..."
              className="h-9 w-full pl-8 pr-3 rounded-md border border-line bg-white text-[13px]"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Pays</label>
          <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} className="h-9 rounded-md border border-line bg-white px-2.5 text-[13px]">
            {COUNTRIES.map((c) => <option key={c} value={c}>{c || 'Tous'}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Statut publi.</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-9 rounded-md border border-line bg-white px-2.5 text-[13px]">
            {PUBLI_STATUS.map((s) => <option key={s} value={s}>{s || 'Tous'}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Instrument</label>
          <select value={filterInstrument} onChange={(e) => setFilterInstrument(e.target.value)} className="h-9 rounded-md border border-line bg-white px-2.5 text-[13px]">
            {INSTRUMENT_TYPES.map((t) => <option key={t} value={t}>{t || 'Tous'}</option>)}
          </select>
        </div>
        <div className="ml-auto text-[12px] text-ink-muted">
          <span className="num">{filtered.length}</span> sur <span className="num">{programmes.length}</span>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border border-line-soft bg-cream shadow-card overflow-hidden">
        {loading && programmes.length === 0 ? (
          <div className="p-3">
            <SkeletonRows rows={6} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[13.5px] text-ink font-medium">
              {programmes.length === 0 ? 'Aucun programme dans la base.' : 'Aucun résultat pour ces filtres.'}
            </p>
            <p className="mt-1 text-[12px] text-ink-muted">
              {programmes.length === 0 ? 'Click "Add programme" pour créer le premier.' : 'Essaye de réinitialiser les filtres.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-wider text-ink-faint font-medium bg-cream-tint border-b border-line-soft">
                <tr>
                  <th className="px-3 py-2 text-left">Nom</th>
                  <th className="px-3 py-2 text-left">Opérateur</th>
                  <th className="px-3 py-2 text-left">Pays</th>
                  <th className="px-3 py-2 text-left">Instrument</th>
                  <th className="px-3 py-2 text-right num">Max</th>
                  <th className="px-3 py-2 text-left">Statut publi.</th>
                  <th className="px-3 py-2 text-left">Dern. vérif</th>
                  <th className="px-3 py-2 text-left w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft">
                {filtered.map((p) => {
                  const bilingueOk = !!(p.nameFr && p.nameEn);
                  return (
                    <tr key={p.id} onClick={() => setEditing(p)} className="hover:bg-cream-tint cursor-pointer">
                      <td className="px-3 py-2 text-ink">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate max-w-[18rem]">
                            {p.nameFr || p.nameEn || <span className="italic text-ink-faint">sans nom</span>}
                          </span>
                          {!bilingueOk && (
                            <span title="Parité bilingue incomplète" className="text-amber text-[10px] font-bold">FR/EN</span>
                          )}
                        </div>
                        {p.idProgramme && <div className="text-[11px] text-ink-faint font-mono">{p.idProgramme}</div>}
                      </td>
                      <td className="px-3 py-2 text-ink-muted truncate max-w-[12rem]">{p.operator || '-'}</td>
                      <td className="px-3 py-2 text-ink-muted">{p.country || '-'}</td>
                      <td className="px-3 py-2 text-ink-muted">{p.instrumentType || '-'}</td>
                      <td className="px-3 py-2 text-right num text-ink">{formatAmount(p.maxAmount)}</td>
                      <td className="px-3 py-2">
                        {p.publicationStatus ? (
                          <span className={'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ' + (STATUS_STYLE[p.publicationStatus] || 'bg-cream-tint text-ink-muted')}>
                            {p.publicationStatus}
                          </span>
                        ) : <span className="text-ink-faint">-</span>}
                      </td>
                      <td className="px-3 py-2 text-ink-faint text-[12px]">
                        {p.lastVerifiedDate ? relativeDate(p.lastVerifiedDate) : '-'}
                      </td>
                      <td className="px-3 py-2 text-ink-faint">
                        <IconChevronRight className="w-3 h-3" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit drawer */}
      {editing && (
        <ProgrammeEditDrawer
          programme={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}
